import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import { Subject, takeUntil } from 'rxjs';
import {
  CRM_LEAD_STATUSES,
  CRM_SOURCE_LABELS,
  CRM_STATUS_LABELS,
  CRM_TABLE_STATUS_LABELS,
  CrmLead,
  CrmLeadStatus,
} from '../../../models/crm-lead.model';
import { ResolvedAddress } from '../../../models/resolved-address.model';
import { AdminCrmDeleteLeadModalComponent } from '../admin-crm-delete-lead-modal/admin-crm-delete-lead-modal.component';
import { AdminCrmLeadOnboardingModalComponent } from '../admin-crm-lead-onboarding-modal/admin-crm-lead-onboarding-modal.component';
import { CrmLeadService } from '../../../services/crm-lead.service';

@Component({
  selector: 'app-admin-crm-leads',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    AdminCrmLeadOnboardingModalComponent,
    AdminCrmDeleteLeadModalComponent,
  ],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './admin-crm-leads.component.html',
})
export class AdminCrmLeadsComponent implements OnInit, OnDestroy {
  private readonly crm = inject(CrmLeadService);
  private readonly router = inject(Router);
  private readonly destroy$ = new Subject<void>();

  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);
  readonly savingId = signal<string | null>(null);
  readonly showOnboarding = signal(false);
  readonly importing = signal(false);
  readonly importFeedback = signal<string | null>(null);
  readonly deleteTarget = signal<CrmLead | null>(null);
  readonly deleting = signal(false);

  readonly leads = signal<CrmLead[]>([]);
  readonly search = signal('');
  readonly statusFilter = signal<CrmLeadStatus | 'all'>('all');
  readonly tasksTodayOnly = signal(false);

  readonly CRM_STATUS_LABELS = CRM_STATUS_LABELS;
  readonly CRM_TABLE_STATUS_LABELS = CRM_TABLE_STATUS_LABELS;
  readonly CRM_LEAD_STATUSES = CRM_LEAD_STATUSES;

  readonly kpis = computed(() => {
    const all = this.leads();
    const interested = all.filter(l =>
      ['qualified', 'committed', 'registered', 'draft', 'live', 'activated'].includes(l.status),
    ).length;
    const blocked = all.filter(l => l.status === 'lost').length;
    return {
      total: all.length,
      interested,
      blocked,
    };
  });

  readonly filteredLeads = computed(() => {
    const q = this.search().trim().toLowerCase();
    const status = this.statusFilter();
    const todayOnly = this.tasksTodayOnly();
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);

    return this.leads().filter(lead => {
      if (status !== 'all' && lead.status !== status) return false;
      if (todayOnly && !this.isActionToday(lead, start, end)) return false;
      if (!q) return true;
      const haystack = [
        lead.venueName,
        lead.city,
        lead.contactName,
        lead.phone,
        lead.email,
        lead.sourceDetail,
        CRM_SOURCE_LABELS[lead.source],
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  });

  ngOnInit(): void {
    this.crm.watchLeads()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: leads => {
          this.leads.set(leads);
          this.loading.set(false);
          this.loadError.set(null);
        },
        error: err => {
          this.loading.set(false);
          const msg = err?.message ?? '';
          this.loadError.set(
            msg.includes('permission') || msg.includes('Permission')
              ? 'Sin permiso en Firestore (/crm_leads). Comprueba claim admin o role admin en tu usuario, cierra sesión y vuelve a entrar. Si acabas de cambiar rules, despliégalas.'
              : (msg || 'No se pudieron cargar los leads.'),
          );
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openLead(id: string): void {
    void this.router.navigate(['/admin/crm/leads', id]);
  }

  createLead(): void {
    this.importFeedback.set(null);
    this.showOnboarding.set(true);
  }

  closeOnboarding(): void {
    if (this.importing()) return;
    this.showOnboarding.set(false);
  }

  onManualLead(): void {
    this.showOnboarding.set(false);
    void this.router.navigate(['/admin/crm/leads/nuevo']);
  }

  async onMapsImported(payload: { resolved: ResolvedAddress; mapsUrl: string }): Promise<void> {
    this.importing.set(true);
    this.importFeedback.set(null);
    try {
      const result = await this.crm.importVenueFromMaps(payload.resolved, payload.mapsUrl);
      this.showOnboarding.set(false);
      this.importFeedback.set(
        result.mergedIntoExisting
          ? 'Teléfono existente: local añadido al contacto.'
          : 'Nuevo contacto y local creados.',
      );
      void this.router.navigate(['/admin/crm/leads', result.leadId]);
    } catch (err: unknown) {
      this.importFeedback.set(err instanceof Error ? err.message : 'Error al importar desde Maps.');
    } finally {
      this.importing.set(false);
    }
  }

  venueCountLabel(lead: CrmLead): string | null {
    const n = lead.venueCount ?? 1;
    return n > 1 ? `+${n - 1} local${n > 2 ? 'es' : ''}` : null;
  }

  leadDeleteLabel(lead: CrmLead): string {
    return lead.contactName || lead.venueName || 'este lead';
  }

  openDeleteConfirm(lead: CrmLead, event: Event): void {
    event.stopPropagation();
    this.deleteTarget.set(lead);
  }

  closeDeleteConfirm(): void {
    if (this.deleting()) return;
    this.deleteTarget.set(null);
  }

  async confirmDeleteLead(): Promise<void> {
    const lead = this.deleteTarget();
    if (!lead) return;
    this.deleting.set(true);
    try {
      await this.crm.deleteLead(lead.id);
      this.deleteTarget.set(null);
    } catch (err: unknown) {
      this.loadError.set(err instanceof Error ? err.message : 'No se pudo eliminar el lead.');
    } finally {
      this.deleting.set(false);
    }
  }

  sourceLabel(lead: CrmLead): string {
    if (lead.mapsUrl) {
      try {
        return new URL(lead.mapsUrl).hostname.replace('www.', '');
      } catch {
        return lead.mapsUrl;
      }
    }
    return CRM_SOURCE_LABELS[lead.source];
  }

  tableStatusLabel(status: CrmLeadStatus): string {
    return CRM_TABLE_STATUS_LABELS[status] ?? CRM_STATUS_LABELS[status];
  }

  tableStatusClass(status: CrmLeadStatus): string {
    if (status === 'lost') return 'bg-red-100 text-red-800';
    if (status === 'contacted') return 'bg-sky-100 text-sky-800';
    if (['qualified', 'committed', 'registered'].includes(status)) {
      return 'bg-emerald-100 text-emerald-800';
    }
    if (status === 'identified') return 'bg-slate-100 text-slate-700';
    return 'bg-indigo-100 text-indigo-800';
  }

  isTaskDue(lead: CrmLead): boolean {
    if (lead.status === 'lost' || lead.status === 'nurturing') return false;
    return lead.nextActionAt?.toMillis?.() < Date.now();
  }

  async blockLead(lead: CrmLead, event: Event): Promise<void> {
    event.stopPropagation();
    if (lead.status === 'lost') return;
    this.savingId.set(lead.id);
    try {
      await this.crm.changeStatus(lead.id, lead.status, 'lost', 'Exclusión manual desde lista.');
    } finally {
      this.savingId.set(null);
    }
  }

  async onStatusChange(lead: CrmLead, next: CrmLeadStatus, event: Event): Promise<void> {
    event.stopPropagation();
    if (next === lead.status) return;
    this.savingId.set(lead.id);
    try {
      await this.crm.changeStatus(lead.id, lead.status, next);
    } finally {
      this.savingId.set(null);
    }
  }

  private isActionToday(lead: CrmLead, start: Date, end: Date): boolean {
    if (!lead.nextActionAt) return false;
    const ms = lead.nextActionAt.toMillis();
    return ms >= start.getTime() && ms <= end.getTime();
  }
}
